import { useEffect, useRef, useState } from "react";
import IC_MORE from "@/assets/record/ic_more.svg";
import { Select, IOptions } from "@/component/Select";
import Dialog from "@/component/Dialog";
import { SimpleRecord, collectRecord, cancelCollectRecord, deleteRecord, RenameRecord, deleteShareRecord, cancelCollectShareRecord, collectShareRecord } from "@/services/api_list";
import COLLECT from "@/assets/record/ic_collect.svg?react";
import { useNavigate } from "react-router-dom";
import { formatRecordTime } from "@/utils/formatter";
import IC_STRIP_PENDING from "@/assets/simulInter/ic_strip_pending.svg";
import IC_STRIP_RECORDING from "@/assets/simulInter/ic_strip_recording.svg";
import IC_STRIP_COMPLETED from "@/assets/simulInter/ic_strip_completed.svg?react";
import { getRatio } from "@/utils/window";
import { RuningInLink } from "@/constants";
import IC_CLOSE_WIN from "@/assets/common/ic_close_win.svg?react";
import { message } from "antd";
import { formatCreateDate } from "../util";
import { recordTitleMaxLen } from "@/constants/record";
import fridayReporter from "@/utils/friday";
import { openShareModalV2 } from "@/sections/WebSummary/ShareModalV2";
import { ControllerStatus } from "@/types/simulInter";

interface IProp {
  isDeleted: boolean | null;
  model: SimpleRecord;
  handleCallback: (newModel: SimpleRecord) => void;
  deleteCallback: () => void;
  cancelCollectCallback: (newModel: SimpleRecord) => void;
}

export default function ItemStrip(prop: IProp) {
  const navigate = useNavigate();
  const { isDeleted, model, handleCallback, deleteCallback, cancelCollectCallback } = prop;
  const [options, setOptions] = useState<Array<IOptions>>([
    { id: "rename", label: "重命名" },
    { id: "collect", label: "收藏" },
    { id: "share", label: "分享" },
    { id: "delete", label: "删除" },
  ]);

  const [showMore, setShowMore] = useState(false);
  const [positioned, setPositioned] = useState<(number | undefined)[]>([0, 0, 0, 0]);
  const [showDialog, setShowDialog] = useState(false);
  const [title, setTitle] = useState("");

  const childRef = useRef<HTMLImageElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const hasFocus = useRef<boolean>(false);

  useEffect(() => {
    setTitle(model.title);
    const newOptions = [...options];
    if (model.collect) {
      const collectIndex = newOptions.findIndex((item) => item.id === "collect");
      if (collectIndex !== -1) {
        newOptions[collectIndex] = { id: "cancelCollect", label: "取消收藏" };
      }
    }
    // 与我分享的，录制中和准备中的不允许重命名
    if (model.shareForMe || model.controllerStatus === ControllerStatus.PENDING || model.controllerStatus === ControllerStatus.RECORDING) {
      const renameIndex = newOptions.findIndex((item) => item.id === "rename");
      newOptions.splice(renameIndex, 1);
    }

    // 录制中和准备中的不允许分享
    if (model.controllerStatus === ControllerStatus.PENDING || model.controllerStatus === ControllerStatus.RECORDING) {
      const shareIndex = newOptions.findIndex((item) => item.id === "share");
      newOptions.splice(shareIndex, 1);
    }

    // 录制中的不允许删除
    if (model.controllerStatus === ControllerStatus.RECORDING) {
      const deleteIndex = newOptions.findIndex((item) => item.id === "delete");
      newOptions.splice(deleteIndex, 1);
    }

    setOptions(newOptions);
  }, []);

  const showMoreHandle = async () => {
    let devicePixelRatio = 1;
    if (RuningInLink) {
      devicePixelRatio = (await getRatio()) ?? 1;
    }
    if (childRef.current) {
      const wh = window.innerHeight - 24 * devicePixelRatio;
      const refY = childRef.current.getBoundingClientRect().y;
      const selectHight = 156 * devicePixelRatio;
      if (selectHight < wh - refY) {
        setPositioned([40 * devicePixelRatio, -24 * devicePixelRatio, undefined, undefined]); // 向下
      } else {
        setPositioned([-selectHight - 4 * devicePixelRatio, -24 * devicePixelRatio, undefined, undefined]); // 向上
      }
      setShowMore(!showMore);
    }
  };

  const choseHandle = async (value: IOptions) => {
    switch (value.id) {
      case "rename": {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
        break;
      }
      case "collect": {
        fridayReporter.pushData({
          eventCode: 'HomePage_ListManage_Favorite'
        })
        let res: boolean;
        if (model.shareForMe) {
          res = await collectShareRecord(model.streamName);
        } else {
          res = await collectRecord(model.streamName);
        }

        if (res) {
          options[1] = { id: "cancelCollect", label: "取消收藏" };
          setOptions(options);
          model.collect = true;
          handleCallback(model);
          message.success("收藏成功");
        } else {
          message.error("收藏失败");
        }
        break;
      }
      case "cancelCollect": {
        let res: boolean;
        if (model.shareForMe) {
          res = await cancelCollectShareRecord(model.streamName);
        } else {
          res = await cancelCollectRecord(model.streamName);
        }

        if (res) {
          options[1] = { id: "collect", label: "收藏" };
          setOptions(options);
          model.collect = false;
          cancelCollectCallback(model);
          message.success("已取消收藏");
        } else {
          message.error("取消收藏失败");
        }
        break;
      }
      case "share": {
        fridayReporter.pushData({
          eventCode: 'HomePage_ListManage_Share'
        })
        // if (model.shareForMe) {
        //   navigator?.clipboard?.writeText(`${model.shareUrl} 《${model.title}》}`);
        //   message.success("复制成功");
        // } else {
        openShareModalV2({
          streamName: model.streamName,
          title: model.title,
        });
        // }
        break;
      }
      case "delete": {
        setShowDialog(true);
        break;
      }
    }
    setShowMore(!showMore);
  };

  const inputBlur = async () => {
    hasFocus.current = false;
    if (model.title === title) return;
    const res = await RenameRecord(model.streamName, title);
    if (res) {
      model.title = title;
      handleCallback(model);
      fridayReporter.pushData({
        eventCode: 'HomePage_ListManage_Rename'
      })
    } else {
      setTitle(model.title);
    }
  };

  const onInputChange = (content: string) => {
    setTitle(content);
  };

  const cancleDlelete = () => {
    setShowDialog(false);
  };

  const confirmDlelete = async () => {
    fridayReporter.pushData({
      eventCode: 'HomePage_ListManage_Delete'
    })
    setShowDialog(false);
    let res: boolean;
    if (model.shareForMe) {
      res = await deleteShareRecord(model.streamName);
    } else {
      res = await deleteRecord(model.streamName);
    }
    if (res) {
      message.success("删除成功");
      deleteCallback();
    } else {
      message.error("删除失败");
    }
  };

  const itemHandle = () => {
    if (isDeleted) return;
    if (hasFocus.current) return;
    fridayReporter.pushData({
      eventCode: 'HomePage_EnterSummaryPage'
    })

    // 只有准备中和录制中状态才跳转到同传页面
    const isPendingOrRecording = model.controllerStatus === ControllerStatus.PENDING ||
      model.controllerStatus === ControllerStatus.RECORDING;

    if (isPendingOrRecording) {
      const params = new URLSearchParams({
        streamName: model.streamName,
        createdDate: model.createdDate,
        title: model.title,
        controllerStatus: model.controllerStatus.toString(),
        isFromList: "true"
      });
      // 如果有boardSn，则添加到参数中
      if (model.boardSn) {
        params.append('boardSn', model.boardSn);
      }
      navigate({
        pathname: "/simulInter",
        search: `?${params.toString()}`
      });
    } else {
      navigate({
        pathname: "/summary",
        search: `?streamId=${model.streamName.replace("live/", "")}&isFromList=true`,
      });
    }
  };

  return (
    <div
      className="h-22 bg-white rd-2.5 pr-6 pl-4 py-4 flex flex-items-center relative hover:shadow-[0px_2px_12px_0px_#191D261F] cursor-pointer"
      onClick={itemHandle}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {(model.controllerStatus === ControllerStatus.COMPLETED && !model.viewed) && (<div className="absolute top--.5 right--.5 w-3 h-3 bg-#FF887A rounded-full"></div>)}
      {model.collect && <COLLECT className="w-5 h-5 absolute top-0 left-0"></COLLECT>}
      <Dialog isOpen={showDialog} title="确定删除" content="删除后，将无法恢复" onCancel={cancleDlelete} onConfirm={confirmDlelete}></Dialog>
      {/* 根据controllerStatus判断显示内容 */}
      {!isDeleted && (
        model.controllerStatus === ControllerStatus.PENDING ? (
          // 待录制状态：显示待录制占位图
          <img className="w-14 h-14 rd-1 mr-4 object-contain" src={IC_STRIP_PENDING} />
        ) : model.controllerStatus === ControllerStatus.RECORDING ? (
          // 正在录制状态：显示正在录制占位图
          <img className="w-14 h-14 rd-1 mr-4 object-contain" src={IC_STRIP_RECORDING} />
        ) : (
          // 完成状态：显示封面或音频图标
          model.coverUrl ? (
            <img className="w-14 h-14 rd-1 mr-4 object-cover bg-#F4F6F9" src={model.coverUrl} />
          ) : (
            <div className="w-14 h-14 rd-1 bg-#F4F6F9 mr-4 flex justify-center items-center">
              <IC_STRIP_COMPLETED className="w-6 h-6" />
            </div>
          )
        )
      )}
      {isDeleted ? (
        <div className="flex-1 flex flex-col flex-justify-center mr-6">
          <span className="text-size-3.5 font-400 text-mx-secondary mb-2">抱歉，此内容无法查看</span>
          <span className="text-size-3 font-400 text-mx-quaternary">源文件已删除</span>
        </div>
      ) : (
        <div className="flex-1 flex flex-col flex-justify-center mr-6">
          <input
            ref={inputRef}
            className="max-w-85 ml--1 h-7.5 text-size-3.75 font-500 text-mx-secondary mb-1 focus:outline-blue bg-transparent border-0 rd-1 p-1 truncate"
            value={title}
            maxLength={recordTitleMaxLen}
            onBlur={inputBlur}
            tabIndex={-1}
            disabled={model.shareForMe || model.controllerStatus === ControllerStatus.PENDING || model.controllerStatus === ControllerStatus.RECORDING}
            onChange={(e) => {
              onInputChange(e.target.value);
            }}
            onFocus={(e) => {
              hasFocus.current = true;
              e.currentTarget.select();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.key === "Enter" && inputRef.current?.blur();
            }}
          />
          <div className="text-size-3 text-mx-quaternary lh-5.5 line-clamp-1 max-w-85">
            <span>{model.controllerStatus === ControllerStatus.PENDING ?
              (model.reportFirstSentence ?? "暂无发言稿") :
              model.summaryRecord.meetingSummary == null ? "等待内容生成" : model.summaryRecord.meetingSummary[0]}</span>
          </div>
        </div>
      )}
      {!isDeleted && <span className="w-30 mr-12 text-mx-quaternary text-size-3.5 font-400">{model.origin}</span>}
      {!isDeleted && <span className="w-25 mr-12 text-mx-quaternary text-size-3.5 font-400">{formatCreateDate(model.createdDate)}</span>}
      {/* 根据controllerStatus显示不同状态 */}
      {!isDeleted && (
        <span className="w-15 mr-12 text-mx-quaternary text-size-3.5 font-400">
          {model.controllerStatus === ControllerStatus.PENDING ? (
            "准备中"
          ) : model.controllerStatus === ControllerStatus.RECORDING ? (
            <span className="text-mx-primary">录制中…</span>
          ) : (
            formatRecordTime(model.duration ?? 0)
          )}
        </span>
      )}
      {!isDeleted && (
        <div className={`relative rd-2 ${showMore ? "bg-#F4F6F9" : ""} flex flex-items-center flex-justify-center`}>
          {model.controllerStatus !== ControllerStatus.RECORDING ? (
            <img
              ref={childRef}
              className="w-8 h-8 hover:bg-#191D260D rd-1.5"
              src={IC_MORE}
              onClick={(e) => {
                e.stopPropagation();
                showMoreHandle();
              }}
            ></img>
          ) : <div className="w-8 h-8"></div>}
          {showMore && (
            <Select
              options={options}
              positioned={positioned}
              onChose={choseHandle}
              onOutsideCb={() => {
                setShowMore(!showMore);
              }}
            ></Select>
          )}
        </div>
      )}
      {isDeleted && (
        <IC_CLOSE_WIN
          className="w-5 h-5"
          onClick={(e) => {
            e.stopPropagation();
            confirmDlelete();
          }}
        ></IC_CLOSE_WIN>
      )}
    </div>
  );
}
